import React, { useCallback } from 'react'
import { Input, Select } from '@root/components'
import { FormProvider, get, useFormContext, useWatch } from 'react-hook-form'
import {
  INVOICE_RECURRENCE_MODE_OPTIONS,
  INVOICE_RECURRENCE_PRESET_OPTIONS,
  INVOICE_REFERENCE_METHODS,
  INVOICE_REPETITION_DAYS,
  INVOICE_REPETITION_MONTHS,
  InvoiceRecurrenceMode,
  InvoiceRecurrencePreset,
  InvoiceRepetitionOptions,
  getRuleFieldsForRecurrencePreset
} from '@root/constants'
import { useTranslation } from 'react-i18next'
import { IInvoiceFormValues } from '@root/query'
import dayjs from 'dayjs'
import theme from '@root/styles/theme'
import styled from 'styled-components'
import SendInvoiceFormBase from '../SendInvoiceFormBase'

interface Props {
  invoiceReceiver: number
  toggleAutoOpen?: boolean
  isCreditNote: boolean
}

const MIN_RECURRENCE_INTERVAL = 1
const MAX_RECURRENCE_INTERVAL = 24

export const InvoiceFormRecurrenceSection: React.FC<Props> = ({
  invoiceReceiver,
  isCreditNote
}) => {
  const {
    register,
    control,
    setValue,
    getValues,
    formState: { errors }
  } = useFormContext<IInvoiceFormValues>()

  const methods = useFormContext()

  const [t] = useTranslation()

  const [recurrenceMode, recurrencePreset, repetitionMonth, invoicingDate] = useWatch({
    control,
    name: [
      'recurrence_mode',
      'recurrence_interval_preset',
      'recurrence_rule.rule.by_month',
      'invoicing_date'
    ]
  })

  const generateDefaultValueForRecurrenceDate = (invoicingDate: string) => {
    return parseInt(invoicingDate?.split('-')[2])
  }

  const defaultValueForRecurrenceDate = generateDefaultValueForRecurrenceDate(invoicingDate)

  const dateValidation = useCallback(date => {
    if (date === '') {
      return true
    }

    const dateObj = dayjs(date, 'YYYY-MM-DD')
    if (!dateObj.isValid()) {
      return t('validation.invalidDate')
    }

    if (dateObj.isBefore(dayjs(new Date()))) {
      return t('validation.dateInFuture')
    }

    return true
  }, [])

  const intervalValidation = useCallback(
    (value: number) => {
      if (recurrencePreset !== InvoiceRecurrencePreset.CUSTOM) {
        return true
      }

      if (!value || value < MIN_RECURRENCE_INTERVAL || value > MAX_RECURRENCE_INTERVAL) {
        return t('invoicing.form.repetition.customIntervalValidation', {
          min: MIN_RECURRENCE_INTERVAL,
          max: MAX_RECURRENCE_INTERVAL
        })
      }

      return true
    },
    [recurrencePreset, t]
  )

  const applyPresetToRule = useCallback(
    (preset: InvoiceRecurrencePreset) => {
      const { frequency, interval } = getRuleFieldsForRecurrencePreset(
        preset,
        getValues('recurrence_rule.rule.interval')
      )

      setValue('recurrence_rule.rule.frequency', frequency)
      setValue('recurrence_rule.rule.interval', interval)

      if (preset !== InvoiceRecurrencePreset.YEARLY) {
        setValue('recurrence_rule.rule.by_month', [])
      }
    },
    [getValues, setValue]
  )

  const handleModeChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const mode = event.target.value as InvoiceRecurrenceMode

      if (mode === InvoiceRecurrenceMode.NONE) {
        setValue('recurrence_rule.rule.frequency', InvoiceRepetitionOptions.NO_SELECTION)
        setValue('recurrence_rule.rule.interval', 1)
        return
      }

      setValue('recurrence_interval_preset', InvoiceRecurrencePreset.MONTHLY)
      applyPresetToRule(InvoiceRecurrencePreset.MONTHLY)
    },
    [applyPresetToRule, setValue]
  )

  const handlePresetChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const preset = event.target.value as InvoiceRecurrencePreset
      applyPresetToRule(preset)
    },
    [applyPresetToRule]
  )

  const repeats = recurrenceMode === InvoiceRecurrenceMode.REPEATS
  const isYearly = recurrencePreset === InvoiceRecurrencePreset.YEARLY
  const isMonthlyBased = repeats && !isYearly
  const showCustomIntervalInput = repeats && recurrencePreset === InvoiceRecurrencePreset.CUSTOM
  const showMonthSelector = isYearly
  const showDaySelector = (isYearly && repetitionMonth) || isMonthlyBased

  return (
    <FormProvider {...methods}>
      {!isCreditNote && (
        <>
          <Select
            id="invoice-form-recurrence-mode"
            defaultValue={InvoiceRecurrenceMode.NONE}
            label={t('invoicing.form.repetition.label')}
            info={t('invoicing.form.repetition.helperRepetition')}
            errors={get(errors, 'recurrence_mode')}
            {...register('recurrence_mode', {
              onChange: handleModeChange
            })}
          >
            {INVOICE_RECURRENCE_MODE_OPTIONS.map((option, index) => (
              <option key={`invoice_recurrence_mode_${index}`} value={option.value}>
                {t(option.labelKey)}
              </option>
            ))}
          </Select>

          {repeats && (
            <SectionWrapper>
              <StyledTitle>{t('invoicing.form.repetition.sectionTitle')}</StyledTitle>

              <Select
                id="invoice-form-recurrence-preset"
                label={t('invoicing.form.repetition.scheduleLabel')}
                info={t('invoicing.form.repetition.presetHelper')}
                errors={get(errors, 'recurrence_interval_preset')}
                style={{ flex: '1 0 100%' }}
                {...register('recurrence_interval_preset', {
                  onChange: handlePresetChange
                })}
              >
                {INVOICE_RECURRENCE_PRESET_OPTIONS.map((option, index) => (
                  <option key={`invoice_recurrence_preset_${index}`} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </Select>

              {showCustomIntervalInput && (
                <Input
                  id="invoice-form-recurrence-interval"
                  label={t('invoicing.form.repetition.customIntervalLabel')}
                  type="number"
                  min={MIN_RECURRENCE_INTERVAL}
                  max={MAX_RECURRENCE_INTERVAL}
                  info={t('invoicing.form.repetition.customIntervalHelper')}
                  style={{ flex: '1 0 40%' }}
                  errors={get(errors, 'recurrence_rule.rule.interval')}
                  {...register('recurrence_rule.rule.interval', {
                    valueAsNumber: true,
                    shouldUnregister: true,
                    validate: intervalValidation
                  })}
                />
              )}

              {showMonthSelector && (
                <Select
                  id="invoice-form-repetition-month"
                  label={t('invoicing.form.repetition.month')}
                  info={t('invoicing.form.repetition.helperMonth')}
                  errors={get(errors, 'recurrence_rule.rule.by_month.0')}
                  required={showMonthSelector}
                  style={{ flex: '1 0 20%', marginRight: `${theme.spacing.xxs}rem` }}
                  {...register('recurrence_rule.rule.by_month.0', {
                    valueAsNumber: true,
                    shouldUnregister: true,
                    required: { value: true, message: t('validation.required') }
                  })}
                >
                  <option hidden={true} value={null} />
                  {INVOICE_REPETITION_MONTHS.map((option, index) => (
                    <option key={`invoice_repetition_month_${index}`} value={option.value}>
                      {t(option.labelKey)}
                    </option>
                  ))}
                </Select>
              )}

              {showDaySelector && (
                <Select
                  id="invoice-form-repetition-day"
                  label={t('invoicing.form.repetition.day')}
                  info={t('invoicing.form.repetition.helperDay')}
                  required={showDaySelector}
                  errors={get(errors, 'recurrence_rule.rule.by_month_day.0')}
                  defaultValue={defaultValueForRecurrenceDate}
                  style={{
                    flex: showMonthSelector ? '1 0 30%' : '1 0 40%',
                    marginRight: `${theme.spacing.xxs}rem`
                  }}
                  {...register('recurrence_rule.rule.by_month_day.0', {
                    valueAsNumber: true,
                    shouldUnregister: true,
                    required: { value: true, message: t('validation.required') }
                  })}
                >
                  {INVOICE_REPETITION_DAYS.map((option, index) => (
                    <option key={`invoice_repetition_day_${index}`} value={option.value}>
                      {option.labelKey ? t(option.labelKey) : option.value}
                    </option>
                  ))}
                </Select>
              )}

              <Input
                label={t('invoicing.form.repetition.invoicingEnd')}
                type="date"
                info={t('invoicing.form.repetition.invoicingEndInfo')}
                style={{ flex: '1 0 40%' }}
                errors={get(errors, 'recurrence_end')}
                {...register('recurrence_end', {
                  validate: dateValidation,
                  shouldUnregister: true
                })}
              />

              <Select
                id="invoice-form-reference-methods"
                label={t('invoicing.form.reference')}
                info={t('invoicing.form.repetition.referenceInfo')}
                required={true}
                errors={get(errors, 'recurrence_reference_method')}
                {...register('recurrence_reference_method', {
                  shouldUnregister: true,
                  required: { value: true, message: t('validation.required') }
                })}
              >
                <option hidden={true} value={null} />
                {INVOICE_REFERENCE_METHODS.map((option, index) => (
                  <option key={`invoice_repetition_reference_method_${index}`} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </Select>

              <StyledTitle>{t('invoicing.form.repetition.headerSending')}</StyledTitle>
              <SendInvoiceFormBase
                invoiceReceiver={invoiceReceiver}
                emailContentName={'recurrence_email_content'}
                emailSubjectName={'recurrence_email_subject'}
              />
            </SectionWrapper>
          )}
        </>
      )}
    </FormProvider>
  )
}

const StyledTitle = styled.h3`
  flex: 1 0 100%;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.md}rem;
`

const SectionWrapper = styled.div`
  padding: ${({ theme }) => theme.spacing.md}rem;
  border: 3px solid ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
`
